import React, { useState, useEffect } from "react";
import { useVenue } from "@/components/shared/VenueContext";
import hosys from "@/api/api";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ClockPanel from "@/components/time/ClockPanel";
import AdminDayView from "@/components/time/AdminDayView";
import SupervisorDashboard from "@/components/time/SupervisorDashboard";
import WeeklyScheduleView from "@/components/time/WeeklyScheduleView";
import WorkerTimeHistory from "@/components/time/WorkerTimeHistory";
import TimeExport from "@/components/time/TimeExport";
import StaffManager from "@/components/time/StaffManager";
import KioskSettings from "@/components/time/KioskSettings";
import NFCSettings from "@/components/time/NFCSettings";
import MonthlyHoursManager from "@/components/time/MonthlyHoursManager";
import { CalendarDays, Users, Monitor, History, Download, LayoutDashboard, Wifi, ClipboardList, ShieldCheck, TrendingUp, Table2 } from "lucide-react";
import OvertimeControl from "@/components/time/OvertimeControlTab.jsx";
import MonthlyGridEditor from "@/components/time/MonthlyGridEditor";

export default function TimeControl() {
  const { currentVenue, user, userRole, loading: venueLoading } = useVenue();
  const [members, setMembers] = useState([]);
  const [activeTab, setActiveTab] = useState("clock");

  const isAdmin = userRole === "superadmin" || userRole === "admin" || userRole === "supervisor";

  const isSuperAdmin = userRole === "superadmin";

  useEffect(() => {
    if (!currentVenue || !isAdmin) return;
    hosys.entities.VenueMember.filter({ venue_id: currentVenue.id, active: true })
      .then(setMembers);
  }, [currentVenue?.id, isAdmin]);

  if (venueLoading) return <QuickLoader />;

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1 rounded-xl flex-wrap h-auto gap-1">
          <TabsTrigger value="clock" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
            <LayoutDashboard className="w-4 h-4" /> Fitxar
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
            <History className="w-4 h-4" /> Historial
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="admin" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <CalendarDays className="w-4 h-4" /> Vista Admin
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="supervisor" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <ShieldCheck className="w-4 h-4" /> Supervisor
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="export" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <Download className="w-4 h-4" /> Exportar
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="monthly" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <ClipboardList className="w-4 h-4" /> Hores mensuals
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="staff" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <Users className="w-4 h-4" /> Personal
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="kiosk" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <Monitor className="w-4 h-4" /> Kiosk
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="nfc" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <Wifi className="w-4 h-4" /> NFC
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="overtime" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <TrendingUp className="w-4 h-4" /> Hores Extres
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="grid" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <Table2 className="w-4 h-4" /> Regularització
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="clock" className="mt-4">
          <ClockPanel venue={currentVenue} user={user} />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <WorkerTimeHistory isAdmin={isAdmin} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="admin" className="mt-4">
            <AdminDayView venueId={currentVenue?.id} members={members} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="supervisor" className="mt-4">
            <SupervisorDashboard venueId={currentVenue?.id} members={members} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="export" className="mt-4">
            <TimeExport
              venueId={currentVenue?.id}
              venueName={currentVenue?.name}
              members={members}
            />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="monthly" className="mt-4">
            <MonthlyHoursManager venueId={currentVenue?.id} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="staff" className="mt-4">
            <StaffManager venueId={currentVenue?.id} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="kiosk" className="mt-4">
            <KioskSettings venue={currentVenue} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="nfc" className="mt-4">
            <Card className="border-0 shadow-sm p-5">
              <NFCSettings venueId={currentVenue?.id} />
            </Card>
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="overtime" className="mt-4">
            <OvertimeControl />
          </TabsContent>
        )}
        {isSuperAdmin && (
          <TabsContent value="grid" className="mt-4">
            <MonthlyGridEditor venueId={currentVenue?.id} members={members} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}